/**
 * Vocab store — data for the "Your vocabulary" screen and the hub
 * progress card (spec: docs/specs/mobile/vocabulary-screen.mobile.md).
 *
 * Three slices, all scoped to ONE course at a time (`courseId`): the
 * aggregate summary, the merged due-for-review list (vocab +
 * expressions), and one paged browse list (learning OR mastered).
 * Switching course resets everything before loading (VS-006).
 *
 * Selector rules (see rolePlayStore): individual field selectors only;
 * never return a fresh array/object from a selector.
 */

import { create } from 'zustand';

import { api } from '../services/api';
import type { StoreErrorInfo } from '../generated/api/endpoint-errors';
import type {
  ExpressionItemSummary,
  VocabItemSummary,
  VocabSummaryResponse,
} from '../generated/api/http-contracts';
import { createLogger } from '../utils/logger';

const log = createLogger('vocabStore');

const DUE_FETCH_LIMIT = 50;
const LIST_PAGE_SIZE = 50;

export type VocabListKind = 'learning' | 'mastered';

/** One row of the merged due-for-review queue. */
export interface DueListItem {
  kind: 'vocab' | 'expression';
  id: string;
  term: string;
  translation: Record<string, string>;
  status: 'new' | 'learning' | 'mastered';
  srsDueAt?: string;
}

interface VocabState {
  /** Course the loaded data belongs to; actions reset when it changes. */
  courseId: string | null;

  summary: VocabSummaryResponse | null;
  summaryLoading: boolean;
  summaryError: StoreErrorInfo | null;

  dueItems: DueListItem[];
  dueLoading: boolean;
  dueError: StoreErrorInfo | null;

  listKind: VocabListKind;
  listItems: VocabItemSummary[];
  listTotal: number;
  listLoading: boolean;
  listLoadingMore: boolean;
  listError: StoreErrorInfo | null;

  loadSummary: (courseId: string) => Promise<void>;
  loadDue: (courseId: string) => Promise<void>;
  setListKind: (courseId: string, kind: VocabListKind) => void;
  loadList: (courseId: string) => Promise<void>;
  loadMoreList: (courseId: string) => Promise<void>;
}

const EMPTY_DATA = {
  summary: null,
  summaryError: null,
  dueItems: [] as DueListItem[],
  dueError: null,
  listItems: [] as VocabItemSummary[],
  listTotal: 0,
  listError: null,
};

function toDueItem(v: VocabItemSummary): DueListItem {
  return {
    kind: 'vocab',
    id: v.id,
    term: v.lemma,
    translation: v.translation,
    status: v.status,
    srsDueAt: v.srsDueAt,
  };
}

function expressionToDueItem(e: ExpressionItemSummary): DueListItem {
  return {
    kind: 'expression',
    id: e.id,
    term: e.text,
    translation: e.translation,
    status: e.status,
    srsDueAt: e.srsDueAt,
  };
}

export const useVocabStore = create<VocabState>((set, get) => {
  /** Reset all data slices when the active course changes (VS-006). */
  function ensureCourse(courseId: string): void {
    if (get().courseId !== courseId) {
      set({ courseId, ...EMPTY_DATA });
    }
  }

  return {
    courseId: null,
    summaryLoading: false,
    dueLoading: false,
    listKind: 'learning',
    listLoading: false,
    listLoadingMore: false,
    ...EMPTY_DATA,

    loadSummary: async (courseId: string) => {
      ensureCourse(courseId);
      set({ summaryLoading: true, summaryError: null });
      const result = await api.getVocabSummary({ params: { courseId } });
      if (get().courseId !== courseId) return; // course switched mid-flight
      if (result.ok) {
        set({ summary: result.data, summaryLoading: false });
      } else {
        log.warn('Failed to load vocab summary', { kind: result.kind, courseId });
        set({ summaryLoading: false, summaryError: result });
      }
    },

    loadDue: async (courseId: string) => {
      ensureCourse(courseId);
      set({ dueLoading: true, dueError: null });
      const params = { courseId, dueForReview: 'true', limit: DUE_FETCH_LIMIT };
      const [vocabResult, exprResult] = await Promise.all([
        api.listVocabItems({ params }),
        api.listExpressionItems({ params }),
      ]);
      if (get().courseId !== courseId) return;
      if (vocabResult.ok && exprResult.ok) {
        const merged = [
          ...vocabResult.data.items.map(toDueItem),
          ...exprResult.data.items.map(expressionToDueItem),
        ].sort((a, b) => (a.srsDueAt ?? '').localeCompare(b.srsDueAt ?? ''));
        set({ dueItems: merged, dueLoading: false });
      } else {
        const failed = !vocabResult.ok ? vocabResult : (exprResult as StoreErrorInfo);
        log.warn('Failed to load due-for-review list', {
          kind: (failed as { kind: string }).kind,
          courseId,
        });
        set({ dueLoading: false, dueError: failed as StoreErrorInfo });
      }
    },

    setListKind: (courseId: string, kind: VocabListKind) => {
      ensureCourse(courseId);
      if (get().listKind === kind && get().listItems.length > 0) return;
      set({ listKind: kind, listItems: [], listTotal: 0, listError: null });
      void get().loadList(courseId);
    },

    loadList: async (courseId: string) => {
      ensureCourse(courseId);
      const kind = get().listKind;
      set({ listLoading: true, listError: null });
      const result = await api.listVocabItems({
        params: { courseId, status: kind, limit: LIST_PAGE_SIZE, offset: 0 },
      });
      if (get().courseId !== courseId || get().listKind !== kind) return;
      if (result.ok) {
        set({ listItems: result.data.items, listTotal: result.data.total, listLoading: false });
      } else {
        log.warn('Failed to load vocab list', { kind: result.kind, courseId, status: kind });
        set({ listLoading: false, listError: result });
      }
    },

    loadMoreList: async (courseId: string) => {
      const { listItems, listTotal, listLoadingMore, listLoading, listKind } = get();
      if (listLoadingMore || listLoading || listItems.length >= listTotal) return;
      set({ listLoadingMore: true });
      const result = await api.listVocabItems({
        params: { courseId, status: listKind, limit: LIST_PAGE_SIZE, offset: listItems.length },
      });
      if (get().courseId !== courseId || get().listKind !== listKind) return;
      if (result.ok) {
        set({
          listItems: [...get().listItems, ...result.data.items],
          listTotal: result.data.total,
          listLoadingMore: false,
        });
      } else {
        // Pagination failures are non-fatal: keep the loaded page, log it.
        log.warn('Failed to load more vocab items', { kind: result.kind, courseId });
        set({ listLoadingMore: false });
      }
    },
  };
});

// ── Selectors (individual, stable) ────────────────────────────────

export const selectVocabSummary = (s: VocabState) => s.summary;
export const selectVocabSummaryLoading = (s: VocabState) => s.summaryLoading;
export const selectVocabSummaryError = (s: VocabState) => s.summaryError;
export const selectVocabDueItems = (s: VocabState) => s.dueItems;
export const selectVocabDueLoading = (s: VocabState) => s.dueLoading;
export const selectVocabDueError = (s: VocabState) => s.dueError;
export const selectVocabListKind = (s: VocabState) => s.listKind;
export const selectVocabListItems = (s: VocabState) => s.listItems;
export const selectVocabListTotal = (s: VocabState) => s.listTotal;
export const selectVocabListLoading = (s: VocabState) => s.listLoading;
export const selectVocabListLoadingMore = (s: VocabState) => s.listLoadingMore;
export const selectVocabListError = (s: VocabState) => s.listError;
export const selectLoadVocabSummary = (s: VocabState) => s.loadSummary;
export const selectLoadVocabDue = (s: VocabState) => s.loadDue;
export const selectSetVocabListKind = (s: VocabState) => s.setListKind;
export const selectLoadVocabList = (s: VocabState) => s.loadList;
export const selectLoadMoreVocabList = (s: VocabState) => s.loadMoreList;
